// #336 slice 5, brief §5.4b: the eight presets of the «Regola» sheet. A
// preset is a precompilation of the one builder (§5.4): its fields, each
// with its step, and the rule they make. Opening the sheet on a slot fills
// the fields from the rule the slot follows, or from its first round.
// Keep free of React and any UI import.
import type { IntensityKind, SlotDraft } from "./programEdit.js";
import { convertEffort, type EffortScale } from "./effortScale.js";
import { seedRuleForRole } from "./programDayRole.js";
import {
  DEFAULT_LOAD_STEP,
  DEFAULT_START_LOAD,
  defaultDeload,
  effortOn,
  harderSign,
  isEffortAxis,
  loadStepFor,
  readRound,
  seqAt,
  type Sequence,
  type ProgressionRule,
} from "./programProgression.js";
import { referenceLoadable, roundReferenceKg } from "./programProgressionReference.js";
import type { MaxForProposal } from "./resolveDefaultWeight.js";
import type { UnitsPreference } from "./schemas/profile.js";
import type { Recognition, RuleName } from "./programProgressionRecognize.js";
import type { RoundType } from "./schemas/programV3.js";
import { fieldNum, fitPyramid, presetFieldsFor, type PresetId, type PresetValues } from "./programProgressionFields.js";


/** The rule a preset makes. The UI goes through presetRuleOn, never this directly. Effort fields are on `scale`, the row's own
 *  effort axis (sheetScaleOf); a ramp's `step` is how much harder each
 *  round gets, so it goes down on RIR.
 *  A field not given takes the value of an exercise added to a day with no
 *  role (seedRuleForRole), or the brief's example. */
export const presetRule = (id: PresetId, v: PresetValues, scale: EffortScale): ProgressionRule => {
  const seed = seedRuleForRole(null, "accessory", scale);
  const seedEffort = seed.value?.kind === "constant" ? seed.value.value : effortOn(7, scale);
  const sets = Math.max(1, fieldNum(v, "sets", seed.sets));
  const reps = fieldNum(v, "reps", seed.reps.kind === "constant" ? seed.reps.value : 10);
  const harder = harderSign(scale);
  const effort = (k: string, dflt: number) => fieldNum(v, k, dflt);
  const constant = (value: number) => ({ kind: "constant" as const, value });
  // #356: `effortAt`, a triple on the row's effort: it holds, the reps and sets move.
  const threshold = (setsMax?: number, effortAt?: number): ProgressionRule => {
    const repsMin = fieldNum(v, "repsMin", 8);
    const repsMax = fieldNum(v, "repsMax", 12);
    const axis = effortAt === undefined ? "absolute" : scale;
    return {
      axis, sets, reps: constant(repsMin), value: null, range: { min: repsMin, max: repsMax },
      threshold: {
        repsMin, repsMax, repsStep: Math.max(1, fieldNum(v, "repsStep", 2)),
        ...(setsMax !== undefined && setsMax > sets ? { setsMax } : {}),
        ...(effortAt === undefined
          ? { loadStart: fieldNum(v, "load", DEFAULT_START_LOAD), loadStep: fieldNum(v, "loadStep", DEFAULT_LOAD_STEP) }
          : { loadStart: effortAt, loadStep: 0 }),
      },
      // Round types (§5.3): the test round's last set is AMRAP, the deload round eases the load.
      test: { amrapLast: true }, deload: defaultDeload(axis),
    };
  };
  switch (id) {
    case "constant":
      return { axis: scale, sets, reps: constant(reps), value: constant(effort("effort", seedEffort)) };
    case "effortRamp":
      return {
        axis: scale, sets, reps: constant(reps),
        value: { kind: "linear", start: effort("effort", seedEffort), step: harder * fieldNum(v, "step", 1), cap: effort("cap", effortOn(9, scale)) },
        test: { amrapLast: true }, deload: defaultDeload(scale),
      };
    case "linearKg":
      return {
        axis: "absolute", sets, reps: constant(reps),
        value: { kind: "linear", start: fieldNum(v, "load", DEFAULT_START_LOAD), step: fieldNum(v, "step", DEFAULT_LOAD_STEP) },
        deload: { factor: fieldNum(v, "deloadPct", (defaultDeload("absolute").factor ?? 1) * 100) / 100 },
      };
    case "repsThenLoad":
      return threshold();
    case "triple":
      return threshold(fieldNum(v, "setsMax", sets + 1), typeof v.effort === "number" ? v.effort : undefined);
    case "timed": {
      // No effort field: an effort the row already has is carried (presetValuesFor).
      const seconds = { kind: "linear" as const, start: fieldNum(v, "seconds", 30), step: fieldNum(v, "step", 5) };
      const e = v.effort;
      return typeof e === "number"
        ? { axis: scale, work: "countdown", sets, reps: seconds, value: constant(e) }
        : { axis: "none", work: "countdown", sets, reps: seconds, value: null };
    }
    case "history":
      return { axis: "history", sets, reps: constant(reps), value: null };
    case "waves": {
      const cycle = Array.isArray(v.efforts) && v.efforts.length > 0 ? v.efforts : [7, 8, 9, 6].map((r) => effortOn(r, scale));
      return { axis: scale, sets, reps: constant(reps), value: { kind: "cycle", values: cycle } };
    }
    case "pyramid": {
      // «Per serie» on two parameters: reps go one way set by set, the load the other.
      const rs = fieldNum(v, "repsSetStep", -2);
      const ls = fieldNum(v, "loadSetStep", DEFAULT_LOAD_STEP);
      const perSet = { ...(rs ? { reps: byStep(sets, rs) } : {}), ...(ls ? { value: byStep(sets, ls) } : {}) };
      return {
        axis: "absolute", sets, reps: constant(fieldNum(v, "reps", 12)),
        value: { kind: "linear", start: fieldNum(v, "load", DEFAULT_START_LOAD), step: fieldNum(v, "step", DEFAULT_LOAD_STEP) },
        ...(rs || ls ? { perSet } : {}),
        deload: { factor: fieldNum(v, "deloadPct", 90) / 100 },
      };
    }
    case "repsUp": {
      const cap = fieldNum(v, "cap", 0);
      return {
        axis: "none", sets, reps: { kind: "linear", start: reps, step: fieldNum(v, "step", 1), ...(cap > 0 ? { cap } : {}) },
        value: null, deload: { setsDelta: -1 },
      };
    }
    case "topBackoff": {
      // «Per serie» on the effort: one top set, N back-off sets easier.
      const n = Math.max(1, fieldNum(v, "backoffSets", 3));
      const top = effort("topEffort", effortOn(8, scale));
      const drop = effort("backoffEffort", top - harder) - top;
      const st = fieldNum(v, "step", 0);
      return {
        axis: scale, sets: n + 1, reps: constant(reps),
        value: st ? { kind: "linear", start: top, step: harder * st } : constant(top),
        ...(drop ? { perSet: { value: [0, ...Array.from({ length: n }, () => drop)] } } : {}),
        deload: defaultDeload(scale),
      };
    }
  }
};

/** 0, d, 2d, … over `n` sets. */
const byStep = (n: number, d: number): number[] => Array.from({ length: n }, (_, i) => Math.round(i * d * 1000) / 1000);

/** True when `xs` is 0, d, 2d, …: the pyramid's own shape. */
const isByStep = (xs: readonly number[] | undefined): boolean =>
  !xs || xs.length < 2 || xs.every((x, i) => Math.abs(x - i * xs[1]!) < 1e-6);

/** True when `xs` is 0, then the same d on every other set. */
const isTopBackoff = (xs: readonly number[] | undefined): boolean =>
  !!xs && xs.length >= 2 && xs[0] === 0 && xs[1] !== 0 && xs.every((x, i) => i === 0 || x === xs[1]);

const EFFORT_AXES: readonly IntensityKind[] = ["rpe", "rir"];

/** The axes a preset writes on. An effort preset writes on the row's own
 *  effort axis (sheetScaleOf), so it reopens a rule on either. */
const PRESET_AXES: Record<PresetId, readonly IntensityKind[]> = {
  constant: EFFORT_AXES,
  effortRamp: EFFORT_AXES,
  waves: EFFORT_AXES,
  topBackoff: EFFORT_AXES,
  timed: [...EFFORT_AXES, "none"],
  linearKg: ["absolute"],
  repsThenLoad: ["absolute"],
  triple: ["absolute", ...EFFORT_AXES],
  pyramid: ["absolute"],
  history: ["history"],
  repsUp: ["none"],
};

const presetByName = (name: RuleName, rule: ProgressionRule): PresetId | null => {
  if (name === "repsThenLoad" || name === "triple" || name === "waves" || name === "timed" || name === "history") return name;
  if (name === "repsUp") return "repsUp";
  const plain = rule.value?.kind === "constant" || (rule.value?.kind === "linear" && rule.value.cap === undefined);
  if (name === "pyramid") {
    return plain && isByStep(rule.perSet?.reps) && isByStep(rule.perSet?.value) ? "pyramid" : null;
  }
  if (name === "topBackoff") return plain && isTopBackoff(rule.perSet?.value) ? "topBackoff" : null;
  if (name === "effortRamp") return "effortRamp";
  if (name === "linear" && rule.value?.kind === "linear") return "linearKg";
  if (name === "constant") return "constant";
  return null;
};

/** The preset that writes a recognized rule again: only one on the rule's
 *  axis, so a threshold at % of the MAX is not reopened in kg, nor a load
 *  cycle as Onde. null: none does. */
export const presetOfRule = (name: RuleName, rule: ProgressionRule): PresetId | null => {
  const id = presetByName(name, rule);
  return id && PRESET_AXES[id].includes(rule.axis) ? id : null;
};

/** The preset a row with no preset of its own opens on, by its axis. A row
 *  at % of the MAX has no preset on its axis: Costante, as before. */
export const defaultPresetFor = (slot: SlotDraft | undefined): PresetId => {
  const row = slot?.setRows[0];
  if (row?.workKind === "countdown") return "timed";
  switch (row?.intensityKind) {
    case "absolute":
      return "linearKg";
    case "history":
      return "history";
    case "none":
      return "repsUp";
    default:
      return "constant";
  }
};

/** The effort scale the «Regola» sheet reads and writes `slot` on: the
 *  row's own, from its rule, else from its first set row. The athlete's
 *  `scale` only for a row with no effort axis (empty, in kg, timed): an RPE
 *  row reopened by a RIR athlete stays RPE, so Apply untouched changes no
 *  round. */
export const sheetScaleOf = (slot: SlotDraft | undefined, rec: Recognition, scale: EffortScale): EffortScale => {
  const own = (k: IntensityKind | undefined): k is EffortScale => k === "rpe" || k === "rir";
  const axis = rec.kind === "rule" ? rec.rule.axis : undefined;
  const row = slot?.setRows[0]?.intensityKind;
  return own(axis) ? axis : own(row) ? row : scale;
};

/** A load on the builder's step, at least one step. */
const alignToStep = (v: number, unit: UnitsPreference): number => {
  const step = loadStepFor(unit);
  return Math.max(step, Math.round(v / step) * step);
};

/** The preset the «Regola» sheet opens on for `slot`. null: a recognized
 *  rule no preset writes (a tetrad, steps by set), or a row by hand with
 *  values in it: the sheet opens with none picked, so Apply untouched cannot
 *  flatten it. An empty row has nothing to protect: its default preset. */
export const startPresetFor = (slot: SlotDraft | undefined, rec: Recognition): PresetId | null => {
  if (rec.kind === "rule") return presetOfRule(rec.name, rec.rule);
  return slot && hasValues(slot) ? null : defaultPresetFor(slot);
};

/** Any round of `slot` with a set that carries a value. */
const hasValues = (slot: SlotDraft): boolean => {
  const timed = slot.setRows[0]?.workKind === "countdown";
  return slot.inactiveWeeks.some((_, w) => (readRound(slot, w, timed) ?? []).some((x) => x.reps !== null || x.value !== null));
};

/** The fields a rule fills, as its preset reads them. An effort is read on
 *  the rule's own axis and moved onto `scale` only when the two differ (on
 *  the sheet they do not: sheetScaleOf); a step keeps its sign, counted as «harder»
 *  (presetRule's own reading). A field the rule does not say is left out. */
const valuesOfRule = (r: ProgressionRule, scale: EffortScale): PresetValues => {
  const v = r.value;
  const t = r.threshold;
  const effortRow = isEffortAxis(r.axis);
  const eff = (x: number): number => (r.axis === "rpe" || r.axis === "rir" ? convertEffort(x, r.axis, scale) : x);
  const out: PresetValues = { sets: r.sets };
  if (r.reps.kind === "constant") out.reps = r.reps.value;
  if (v?.kind === "constant") {
    if (effortRow) out.effort = eff(v.value);
    if (r.axis === "absolute") out.load = v.value;
    if (r.reps.kind !== "linear") out.step = 0;
  }
  if (v?.kind === "linear") {
    if (effortRow) out.effort = eff(v.start);
    else out.load = v.start;
    out.step = v.step * harderSign(r.axis);
    if (v.cap !== undefined) out.cap = eff(v.cap);
  }
  if (v?.kind === "cycle") out.efforts = v.values.map(eff);
  if (r.deload?.factor !== undefined) out.deloadPct = Math.round(r.deload.factor * 100);
  if (r.reps.kind === "linear") Object.assign(out, { seconds: r.reps.start, step: r.reps.step });
  if (t) {
    const at = effortRow ? { effort: eff(t.loadStart) } : { load: t.loadStart, loadStep: t.loadStep };
    Object.assign(out, { repsMin: t.repsMin, repsMax: t.repsMax, repsStep: t.repsStep, ...at, ...(t.setsMax ? { setsMax: t.setsMax } : {}) });
  }
  // Rising reps: reps W1, the step, the cap (0: none).
  if (r.reps.kind === "linear" && !r.work) Object.assign(out, { reps: r.reps.start, cap: r.reps.cap ?? 0 });
  // The pyramid and top and back-off: the steps inside the round.
  const ps = r.perSet;
  const base = v?.kind === "constant" ? v.value : v?.kind === "linear" ? v.start : 0;
  out.repsSetStep = ps?.reps?.[1] ?? 0;
  out.loadSetStep = ps?.value?.[1] ?? 0;
  if (effortRow) {
    Object.assign(out, { topEffort: eff(base), backoffSets: Math.max(1, r.sets - 1), backoffEffort: eff(base + (ps?.value?.[1] ?? 0)) });
  }
  return out;
};

/** Where the load sits on a row in the cells' unit: `ref`. */
export type PresetRef = {
  cellUnit: UnitsPreference;
  max?: MaxForProposal;
  /** The draft's round types: where a rule read back stops (its cap). */
  types?: readonly RoundType[];
};

/** The hardest value an uncapped linear effort reaches over the slot's
 *  rounds, on `scale`: a cap that changes nothing. */
const reachOf = (
  seq: Extract<Sequence, { kind: "linear" }>,
  axis: "rpe" | "rir",
  scale: EffortScale,
  slot: SlotDraft,
  types: readonly RoundType[],
): number => {
  const xs = slot.inactiveWeeks.map((_, w) => seqAt(seq, w, types));
  const top = harderSign(axis) > 0 ? Math.max(...xs) : Math.min(...xs);
  return Math.min(10, Math.max(scale === "rir" ? 0 : 1, convertEffort(top, axis, scale)));
};

/** The fields `id` opens with on `slot`: read from its rule when that rule is
 *  the preset's own, else its first round's sets, reps and value, and the
 *  preset's defaults for the rest, shown (a cap, a deload %), never applied
 *  unseen. A row with no load starts a load preset from its reference load
 *  (the MAX), else DEFAULT_START_LOAD. Every field of the preset has a value. */
export const presetValuesFor = (
  id: PresetId,
  slot: SlotDraft,
  rec: Recognition,
  scale: EffortScale,
  ref?: PresetRef,
): PresetValues => {
  const unit = ref?.cellUnit ?? "kg";
  if (rec.kind === "rule" && presetOfRule(rec.name, rec.rule) === id) {
    const read = valuesOfRule(rec.rule, scale);
    const v = rec.rule.value;
    const axis = rec.rule.axis;
    // A ramp read back with no cap: the cap it reaches anyway, not the 9 of a new one.
    if (id === "effortRamp" && v?.kind === "linear" && v.cap === undefined && (axis === "rpe" || axis === "rir")) {
      read.cap = reachOf(v, axis, scale, slot, ref?.types ?? []);
    }
    return { ...valuesOfRule(presetRule(id, {}, scale), scale), ...read };
  }
  const w = slot.inactiveWeeks.findIndex((off) => !off);
  const timed = slot.setRows[0]?.workKind === "countdown";
  const first = w < 0 ? null : readRound(slot, w, timed);
  const s0 = first?.[0];
  const out: PresetValues = {};
  if (first && first.length > 0) out.sets = first.length;
  // A row that was not a pyramid starts one from its defaults (12, −2), not
  // from its reps: 5 reps less 2 a set reach 0 by the fourth.
  if (s0?.reps != null && timed === (id === "timed") && id !== "pyramid") out[timed ? "seconds" : "reps"] = s0.reps;
  if (s0?.value != null && s0.kind === scale) Object.assign(out, { effort: s0.value, topEffort: s0.value });
  // #356: a triple picked on an effort row stays on its effort.
  const rowAxis = rec.kind === "rule" ? rec.rule.axis : slot.setRows[0]?.intensityKind;
  if (id === "triple" && out.effort === undefined && rowAxis !== undefined && isEffortAxis(rowAxis)) out.effort = effortOn(8, scale);
  const fields = presetFieldsFor(id, presetRule(id, out, scale).axis);
  if (s0?.value != null && s0.kind === "absolute") out.load = s0.value;
  else if (fields.some((x) => x.key === "load")) {
    const kg = w < 0 ? null : roundReferenceKg(slot, w, ref?.max);
    out.load = kg !== null && ref ? alignToStep(referenceLoadable(kg, ref.cellUnit), ref.cellUnit) : DEFAULT_START_LOAD;
  }
  // A load step starts at one step of the cells' unit: 5 in lb, not 2.5.
  for (const fl of fields) if (ref && fl.unit === "load" && fl.delta) out[fl.key] = loadStepFor(ref.cellUnit);
  const values = { ...valuesOfRule(presetRule(id, out, scale), scale), ...out };
  return id === "pyramid" ? fitPyramid(values, unit) : values;
};
